import React, { useEffect, lazy, Suspense } from 'react'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { useAuthStore } from '@/stores/authStore'
import { websocketService } from '@/services/websocketService'
import DashboardLayout from '@/components/layout/DashboardLayout'
import { ErrorBoundary } from '@/components/ui/ErrorBoundary'
import { ToastProvider } from '@/components/ui/Toast'
import { LoadingProvider, PageLoading } from '@/components/ui/LoadingStates'
import { RoleGuard, UserRole } from '@/components/guards/RoleGuard'
import { MarketingLocaleProvider } from '@/i18n/MarketingLocaleProvider'

// Lazy load page components for code splitting
const LoginPage = lazy(() => import('@/pages/LoginPage'))
const LandingPage = lazy(() => import('@/pages/LandingPage'))
const CityVisionPage = lazy(() => import('@/pages/CityVisionPage'))
const PrivacyPolicyPage = lazy(() => import('@/pages/PrivacyPolicyPage'))
const DashboardPage = lazy(() => import('@/pages/DashboardPage'))
const ShiftsPage = lazy(() => import('@/pages/ShiftsPage'))
const MessagesPage = lazy(() => import('@/features/messages/MessagesPage'))
const SettingsPage = lazy(() => import('@/pages/SettingsPage'))
const TasksPage = lazy(() => import('@/pages/TasksPage'))
const UserManagementPage = lazy(() => import('@/pages/UserManagementPage'))
const AnalysisPage = lazy(() => import('@/pages/AnalysisPage'))
const WarehousePage = lazy(() => import('@/pages/WarehousePage'))
const RouteOptimizationPage = lazy(() => import('@/pages/RouteOptimizationPage'))
const DispatchPage = lazy(() => import('@/pages/DispatchPage'))
const DocumentArchivePage = lazy(() => import('@/pages/DocumentArchivePage').then(m => ({ default: m.DocumentArchivePage })))
const ItTicketsPage = lazy(() => import('@/pages/ItTicketsPage'))
const PresidentDashboardPage = lazy(() => import('@/pages/PresidentDashboardPage').then(m => ({ default: m.PresidentDashboardPage })))
const WhatsAppConnectionPage = lazy(() => import('@/pages/WhatsAppConnectionPage'))
const BeyazMasaTicketsPage = lazy(() => import('@/pages/BeyazMasaTicketsPage'))
const BeyazMasaStatsPage = lazy(() => import('@/pages/BeyazMasaStatsPage'))
const BeyazMasaDashboard = lazy(() => import('@/pages/BeyazMasaDashboard'))
const VehiclesPage = lazy(() => import('@/pages/VehiclesPage'))
const ClassificationFeedbackPage = lazy(() => import('@/pages/ClassificationFeedbackPage'))
const ClassificationSuggestionsPage = lazy(() => import('@/pages/ClassificationSuggestionsPage'))
const YaziIsleriPage = lazy(() => import('@/pages/YaziIsleriPage'))

function MarketingPage({ children }: { children: React.ReactNode }) {
  return <MarketingLocaleProvider>{children}</MarketingLocaleProvider>
}

function AppContent() {
  const { isAuthenticated, isLoading } = useAuthStore()
  const location = useLocation()

  useEffect(() => {
    // Initialize WebSocket connection when user is authenticated
    if (isAuthenticated) {
      websocketService.connect()
      // Role/permissions come from persisted session; refresh from server so admin role changes apply without waiting on token expiry.
      void useAuthStore.getState().syncUserFromApi()

      return () => {
        websocketService.disconnect()
      }
    }
  }, [isAuthenticated])

  if (isLoading) {
    return <PageLoading message="Uygulama yükleniyor..." />
  }

  if (location.pathname === '/city-vision') {
    return (
      <ErrorBoundary resetKey={location.pathname}>
        <Suspense fallback={<PageLoading message="Sayfa yükleniyor..." />}>
          <MarketingPage>
            <CityVisionPage />
          </MarketingPage>
        </Suspense>
      </ErrorBoundary>
    )
  }

  if (!isAuthenticated) {
    return (
      <ErrorBoundary resetKey={location.pathname}>
        {/* Suspense required: LandingPage and LoginPage are lazy(); navigation would otherwise suspend during sync input (React #426). */}
        <Suspense fallback={<PageLoading message="Sayfa yükleniyor..." />}>
          <Routes>
            <Route path="/" element={<MarketingPage><LandingPage /></MarketingPage>} />
            <Route path="/city-vision" element={<MarketingPage><CityVisionPage /></MarketingPage>} />
            <Route path="/privacy-policy" element={<MarketingPage><PrivacyPolicyPage /></MarketingPage>} />
            <Route path="/login/:municipalitySlug" element={<LoginPage />} />
            <Route path="/login" element={<MarketingPage><LandingPage /></MarketingPage>} />
            <Route path="*" element={<MarketingPage><LandingPage /></MarketingPage>} />
          </Routes>
        </Suspense>
      </ErrorBoundary>
    )
  }

  return (
    <ErrorBoundary resetKey={location.pathname}>
      <DashboardLayout>
        <Suspense fallback={<PageLoading message="Sayfa yükleniyor..." />}>
          <Routes>
            {/* Dashboard - Executives only */}
            <Route path="/dashboard" element={
              <RoleGuard
                requiredRole={[UserRole.PRESIDENT, UserRole.BASKAN, UserRole.ADMIN]}
                strict={true}
                redirectTo="/shifts"
              >
                <DashboardPage />
              </RoleGuard>
            } />

            {/* President Analytics Dashboard - President only */}
            <Route
              path="/president-analytics"
              element={
                <RoleGuard
                  requiredRole={[UserRole.PRESIDENT, UserRole.BASKAN]}
                  strict={true}
                >
                  <PresidentDashboardPage />
                </RoleGuard>
              }
            />

            {/* Shifts - MEMUR and above */}
            <Route path="/shifts" element={
              <RoleGuard requiredRole={UserRole.MEMUR}>
                <ShiftsPage />
              </RoleGuard>
            } />

            {/* Messages - MEMUR and above */}
            <Route path="/messages" element={
              <RoleGuard requiredRole={UserRole.MEMUR}>
                <MessagesPage />
              </RoleGuard>
            } />

            {/* Tasks - MEMUR and above */}
            <Route path="/tasks" element={
              <RoleGuard requiredRole={UserRole.MEMUR}>
                <TasksPage />
              </RoleGuard>
            } />

            {/* Yazı İşleri Evrak - PERSONEL+ okuma; yazma YAZIM_EVRAK / Başkan / Başkan Yardımcısı / PRESIDENT */}
            <Route
              path="/yazi-isleri"
              element={
                <RoleGuard requiredRole={UserRole.PERSONEL}>
                  <YaziIsleriPage />
                </RoleGuard>
              }
            />

            {/* Analysis - Supervisor and above */}
            <Route path="/analysis" element={
              <RoleGuard requiredRole={UserRole.SUPERVISOR}>
                <AnalysisPage />
              </RoleGuard>
            } />

            {/* Route Optimization - Operator and above */}
            <Route path="/route-optimization" element={
              <RoleGuard requiredRole={UserRole.OPERATOR}>
                <RouteOptimizationPage />
              </RoleGuard>
            } />

            {/* Field dispatch - Supervisor and above */}
            <Route path="/dispatch" element={
              <RoleGuard requiredRole={UserRole.SUPERVISOR}>
                <DispatchPage />
              </RoleGuard>
            } />

            {/* Vehicles - Messenger and above */}
            <Route path="/vehicles" element={
              <RoleGuard requiredRole={UserRole.MESSENGER}>
                <VehiclesPage />
              </RoleGuard>
            } />

            {/* Warehouse - Warehouse users, Admins, and executives */}
            <Route path="/warehouse" element={
              <RoleGuard
                requiredRole={[
                  UserRole.PRESIDENT,
                  UserRole.BASKAN,
                  UserRole.BASKAN_YARDIMCISI,
                  UserRole.ADMIN,
                  UserRole.DEPO_KULLANICISI
                ]}
                strict={true}
              >
                <WarehousePage />
              </RoleGuard>
            } />

            {/* Settings - All authenticated users */}
            <Route path="/settings" element={<SettingsPage />} />

            {/* Users - Admin only */}
            <Route path="/users" element={
              <RoleGuard requiredRole={[UserRole.PRESIDENT, UserRole.ADMIN]} strict={true}>
                <UserManagementPage />
              </RoleGuard>
            } />

            {/* IT Tickets - All authenticated users */}
            <Route path="/it-tickets" element={<ItTicketsPage />} />

            {/* Document Archive - Executives and admins only */}
            <Route
              path="/document-archive"
              element={
                <RoleGuard
                  requiredRole={[
                    UserRole.PRESIDENT,
                    UserRole.BASKAN,
                    UserRole.BASKAN_YARDIMCISI,
                    UserRole.ADMIN
                  ]}
                  strict={true}
                >
                  <DocumentArchivePage />
                </RoleGuard>
              }
            />

            {/* Beyaz Masa Tickets - Department Staff */}
            <Route
              path="/beyaz-masa-tickets"
              element={
                <RoleGuard
                  requiredRole={[
                    UserRole.PRESIDENT,
                    UserRole.BASKAN,
                    UserRole.BASKAN_YARDIMCISI,
                    UserRole.ADMIN,
                    UserRole.FEN_ISLERI,
                    UserRole.TEMIZLIK,
                    UserRole.PARK_BAHCE,
                    UserRole.IMAR_SEHIRCILIK,
                    UserRole.CEVRE,
                    UserRole.SU_KANALIZASYON,
                    UserRole.ULASIM,
                    UserRole.ITFAIYE,
                    UserRole.ZABITA,
                    UserRole.SOSYAL_YARDIM,
                    UserRole.KULTUR_SOSYAL,
                    UserRole.SPOR,
                    UserRole.YAZIM_EVRAK,
                    UserRole.MALI_HIZMETLER,
                    UserRole.BILGI_ISLEM,
                    UserRole.INSAN_KAYNAKLARI,
                    UserRole.ETUD_PROJE,
                    UserRole.HUKUK,
                    UserRole.AFET_ISLERI,
                  ]}
                >
                  <BeyazMasaTicketsPage />
                </RoleGuard>
              }
            />

            {/* Beyaz Masa Statistics - Executives only */}
            <Route
              path="/beyaz-masa/stats"
              element={
                <RoleGuard
                  requiredRole={[
                    UserRole.PRESIDENT,
                    UserRole.BASKAN,
                    UserRole.BASKAN_YARDIMCISI,
                    UserRole.ADMIN,
                    UserRole.GENEL_SEKRETER
                  ]}
                >
                  <BeyazMasaStatsPage />
                </RoleGuard>
              }
            />

            {/* Beyaz Masa Dashboard - Executives only */}
            <Route
              path="/beyaz-masa/dashboard"
              element={
                <RoleGuard
                  requiredRole={[
                    UserRole.PRESIDENT,
                    UserRole.BASKAN,
                    UserRole.BASKAN_YARDIMCISI,
                    UserRole.ADMIN,
                    UserRole.GENEL_SEKRETER
                  ]}
                >
                  <BeyazMasaDashboard />
                </RoleGuard>
              }
            />

            {/* Sınıflandırma Geri Bildirimi - PRESIDENT, BASKAN, ADMIN only */}
            <Route
              path="/classification-feedback"
              element={
                <RoleGuard
                  requiredRole={[UserRole.PRESIDENT, UserRole.BASKAN, UserRole.ADMIN]}
                  strict={true}
                >
                  <ClassificationFeedbackPage />
                </RoleGuard>
              }
            />
            <Route
              path="/classification-feedback/suggestions"
              element={
                <RoleGuard
                  requiredRole={[UserRole.PRESIDENT, UserRole.BASKAN, UserRole.ADMIN]}
                  strict={true}
                >
                  <ClassificationSuggestionsPage />
                </RoleGuard>
              }
            />

            {/* WhatsApp Connection - Admins only */}
            <Route
              path="/whatsapp-connection"
              element={
                <RoleGuard
                  requiredRole={[
                    UserRole.PRESIDENT,
                    UserRole.ADMIN,
                    UserRole.BILGI_ISLEM
                  ]}
                  strict={true}
                >
                  <WhatsAppConnectionPage />
                </RoleGuard>
              }
            />

            <Route path="/privacy-policy" element={<MarketingPage><PrivacyPolicyPage /></MarketingPage>} />
            <Route path="/login" element={<Navigate to="/dashboard" replace />} />
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </Suspense>
      </DashboardLayout>
    </ErrorBoundary>
  )
}

function App() {
  return (
    <ErrorBoundary>
      <ToastProvider>
        <LoadingProvider>
          <AppContent />
        </LoadingProvider>
      </ToastProvider>
    </ErrorBoundary>
  )
}

export default App
